import { GraphQL } from "@rpi/openapi-api";

/**
 * Generic version of the SearchInput type, since each one is currently implemented as a unique type in the GraphQL schema
 */
type BaseSearchInput<TCondition extends GraphQL.SearchCondition | null, TField extends string> = {
  condition: TCondition;
  field: TField;
  firstValue?: string | null;
  secondValue?: string | null;
  values?: (string | null)[] | null;
};

/**
 * SearchInput with condition "EQUALS" - requires only firstValue
 */
export interface EqualsSearchInput<TField extends string>
  extends BaseSearchInput<typeof GraphQL.SearchCondition.Equals, TField> {
  firstValue: string;
  secondValue?: never;
  values?: never;
}
export function isEqualsSearch<T extends string>(search: SearchInput<T>): search is EqualsSearchInput<T> {
  return search.condition === GraphQL.SearchCondition.Equals && !!search.firstValue;
}

/**
 * SearchInput with condition "BETWEEN" - requires both firstValue and secondValue
 */
export interface BetweenSearchInput<TField extends string>
  extends BaseSearchInput<typeof GraphQL.SearchCondition.Between, TField> {
  firstValue: string;
  secondValue: string;
  values?: never;
}
export function isBetweenSearch<T extends string>(search: SearchInput<T>): search is BetweenSearchInput<T> {
  return search.condition === GraphQL.SearchCondition.Between && !!search.firstValue && !!search.secondValue;
}

/**
 * SearchInput with condition "IN" - requires values array
 */
export interface InSearchInput<TField extends string>
  extends BaseSearchInput<typeof GraphQL.SearchCondition.In, TField> {
  values: string[];
  firstValue?: never;
  secondValue?: never;
}
export function isInSearch<T extends string>(search: SearchInput<T>): search is InSearchInput<T> {
  return search.condition === GraphQL.SearchCondition.In && !!search.values && search.values.length > 0;
}

/**
 * SearchInput with condition "CONTAINS" - requires only firstValue
 */
export interface ContainsSearchInput<TField extends string>
  extends BaseSearchInput<typeof GraphQL.SearchCondition.Contains, TField> {
  firstValue: string;
  secondValue?: never;
  values?: never;
}
export function isContainsSearch<T extends string>(search: SearchInput<T>): search is ContainsSearchInput<T> {
  return search.condition === GraphQL.SearchCondition.Contains && !!search.firstValue;
}

/**
 * SearchInput with condition "NOT_EQUALS" - requires only firstValue
 */
export interface NotEqualsSearchInput<TField extends string>
  extends BaseSearchInput<typeof GraphQL.SearchCondition.NotEquals, TField> {
  firstValue: string;
  secondValue?: never;
  values?: never;
}
export function isNotEqualsSearch<T extends string>(search: SearchInput<T>): search is NotEqualsSearchInput<T> {
  return search.condition === GraphQL.SearchCondition.NotEquals && !!search.firstValue;
}

/**
 * Union type of all possible search input variations
 */
export type SearchInput<TField extends string> =
  | EqualsSearchInput<TField>
  | BetweenSearchInput<TField>
  | InSearchInput<TField>
  | ContainsSearchInput<TField>
  | NotEqualsSearchInput<TField>
  | BaseSearchInput<GraphQL.SearchCondition, TField>;

/**
 * Helper function remove null values from the graphQL search input and turn it into a SearchInput
 * @param searchInputs a GraphQL search input type
 * @returns A SearchInput array
 */
export function toSearchInputArray<TField extends string>(
  searchInputs?: (BaseSearchInput<GraphQL.SearchCondition, TField> | null)[] | null
): SearchInput<TField>[] {
  return searchInputs?.filter<SearchInput<TField>>((value): value is SearchInput<TField> => !!value) ?? [];
}

/**
 * Helper function to turn a dateRange into a BetweenSearchInput, for use with the DatePicker component
 * @param field Which searchField input type to use
 * @param dateRange The dateRange
 * @returns BetweenSearchInput or null
 */
export function getDateRangeFilter<TField extends string>(
  field: TField,
  dateRange: [Date | null, Date | null]
): BetweenSearchInput<TField> | null {
  if (dateRange[0] && dateRange[1]) {
    // Set the second value to the end of the day for proper range comparison,
    // as the backend does instance comparison
    const secondValue = new Date(dateRange[1]);
    secondValue.setHours(23, 59, 59);
    return {
      field,
      condition: GraphQL.SearchCondition.Between,
      firstValue: dateRange[0].toISOString(),
      secondValue: secondValue.toISOString(),
    };
  } else {
    return null;
  }
}

/**
 * Helper function to turn an array of enum values into an InSearchInput, for use with the MultiSelect component
 * @param field Which searchField input type to use
 * @param filter the array of selected enum values
 * @returns InSearchInput or null
 */
export function getInStatusFilter<TField extends string, TStatus extends string>(
  field: TField,
  filter: TStatus[]
): InSearchInput<TField> | null {
  if (filter.length > 0) {
    return {
      field,
      condition: GraphQL.SearchCondition.In,
      values: filter,
    };
  } else {
    return null;
  }
}
